/**
 * @engine/physics2d - Core Types
 *
 * Strong types for 2D physics with GPU compute shader compatibility.
 * All numeric types use explicit widths for deterministic GPU layout.
 */

// ============================================================================
// ENUMS (u32-compatible for GPU buffers)
// ============================================================================

/** Physics body simulation type */
export const enum BodyType {
  /** Zero mass, zero velocity — never moves */
  Static = 0,
  /** Zero mass, user-driven velocity — moves but unaffected by forces */
  Kinematic = 1,
  /** Positive mass, fully simulated */
  Dynamic = 2,
}

/** Collider primitive type (maps to GPU dispatch) */
export const enum ColliderType {
  /** Axis-aligned box */
  Box = 0,
  /** Perfect circle */
  Circle = 1,
  /** Box with rounded ends */
  Capsule = 2,
  /** Convex polygon (up to MAX_POLYGON_VERTICES) */
  Polygon = 3,
  /** Edge chain — for tilemap boundaries */
  Edge = 4,
  /** Composite tilemap collider */
  Tilemap = 5,
}

/** Capsule orientation */
export const enum CapsuleDirection {
  /** Height along Y axis */
  Vertical = 0,
  /** Height along X axis */
  Horizontal = 1,
}

/** How to apply force to a body */
export const enum ForceMode2D {
  /** Continuous force (scaled by dt) */
  Force = 0,
  /** Instant velocity change (ignores mass) */
  Impulse = 1,
}

/** Interpolation mode for rendering */
export const enum RigidBodyInterpolation {
  None = 0,
  Interpolate = 1,
  Extrapolate = 2,
}

/** Collision detection mode */
export const enum CollisionDetection2D {
  /** Standard discrete detection */
  Discrete = 0,
  /** Swept CCD for fast-moving bodies */
  Continuous = 1,
}

/** Sleep state for bodies */
export const enum SleepState {
  Awake = 0,
  Sleeping = 1,
}

/** How to combine physics material properties when two colliders interact */
export const enum CombineMode {
  /** Average of the two values */
  Average = 0,
  /** Use the smaller value */
  Minimum = 1,
  /** Use the larger value */
  Maximum = 2,
  /** Multiply the two values */
  Multiply = 3,
}

/** Gravity zone type */
export const enum GravityZoneType {
  /** Replace global gravity with zone's gravity vector */
  Override = 0,
  /** Add zone's gravity to current gravity */
  Additive = 1,
  /** Attract toward zone center (planetary) */
  Point = 2,
  /** Multiply current gravity by scale factor */
  Scale = 3,
}

/** Gravity zone shape */
export const enum GravityZoneShape {
  /** Circular zone defined by inner/outer radius */
  Circle = 0,
  /** Rectangular zone (good for tunnels, corridors) */
  Box = 1,
  /** Capsule zone (stadium shape) */
  Capsule = 2,
  /** Polygon zone (arbitrary convex shape) */
  Polygon = 3,
}

/** Falloff curve for gravity zone strength */
export const enum GravityFalloff {
  /** No falloff — full strength everywhere */
  None = 0,
  /** Linear decrease from inner to outer radius */
  Linear = 1,
  /** Inverse square (realistic for point gravity) */
  InverseSquare = 2,
  /** Smooth hermite interpolation */
  Smoothstep = 3,
}

/** Gravity zone configuration */
export interface GravityZoneConfig {
  /** Zone type */
  type: GravityZoneType;
  /** Zone shape */
  shape: GravityZoneShape;
  /** Gravity vector (for Override/Additive) or strength (for Point/Scale) */
  gravity: PhysVec2;
  /** Strength multiplier (for Point type, or scale factor for Scale type) */
  strength: number;
  /** Falloff type */
  falloff: GravityFalloff;
  /** Inner radius — full strength within this distance from center (Circle/Capsule) */
  innerRadius: number;
  /** Outer radius — zero strength beyond this distance (Circle/Capsule) */
  outerRadius: number;
  /** Size for Box shape, or capsule dimensions (width = diameter, height = total length) */
  size: PhysVec2;
  /** Direction for Capsule (0 = vertical, 1 = horizontal) */
  direction: number;
  /** Polygon points (local space, for Polygon shape) */
  points: PhysVec2[];
  /** Priority for blending (higher = more influence) */
  priority: number;
  /** Layer mask — which bodies are affected */
  affectedLayers: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/** Maximum vertices in a convex polygon (GPU array limit) */
export const MAX_POLYGON_VERTICES = 16;

/** Maximum contact points per collision pair */
export const MAX_CONTACT_POINTS = 2;

/** Default physics layers */
export const PhysicsLayers = {
  Default: 0x0001,
  Player: 0x0002,
  Enemy: 0x0004,
  Projectile: 0x0008,
  Terrain: 0x0010,
  Trigger: 0x0020,
  UI: 0x0040,
  All: 0xFFFF,
} as const;

/** Default solver settings */
export const SOLVER_DEFAULTS = {
  gravity: { x: 0, y: -9.81 } as const,
  fixedTimestep: 1 / 60,
  velocityIterations: 8,
  positionIterations: 3,
  maxLinearVelocity: 200,
  maxAngularVelocity: 360 * Math.PI / 180,
  linearSleepThreshold: 0.01,
  angularSleepThreshold: 2 * Math.PI / 180,
  timeToSleep: 0.5,
  baumgarte: 0.2,
  linearSlop: 0.005,
  angularSlop: 2 * Math.PI / 180,
  maxLinearCorrection: 0.2,
  maxTranslation: 2.0,
  maxRotation: 0.5 * Math.PI,
} as const;

// ============================================================================
// VALUE TYPES
// ============================================================================

/** 2D vector for physics (f32 precision, GPU-compatible) */
export interface PhysVec2 {
  x: number;
  y: number;
}

/** Mass properties computed from shape + density */
export interface MassData {
  /** Total mass (kg) */
  mass: number;
  /** Inverse mass (0 for static) */
  invMass: number;
  /** Rotational inertia */
  inertia: number;
  /** Inverse inertia (0 for static) */
  invInertia: number;
  /** Center of mass in local space */
  center: PhysVec2;
  /** Shape area (for density calculation) */
  area: number;
}

/** Surface material properties */
export interface PhysicsMaterial2D {
  /** Coulomb friction coefficient [0..1] */
  friction: number;
  /** Restitution / bounciness [0..1] */
  bounciness: number;
  /** Mass per unit area (kg/m²) */
  density: number;
  /** How to combine friction when two colliders touch */
  frictionCombine?: CombineMode;
  /** How to combine bounciness when two colliders touch */
  bouncinessCombine?: CombineMode;
}

/** Constraint flags packed as u32 bitfield */
export interface RigidBodyConstraints {
  /** Freeze position on X axis */
  freezePositionX: boolean;
  /** Freeze position on Y axis */
  freezePositionY: boolean;
  /** Freeze rotation */
  freezeRotation: boolean;
}

// ============================================================================
// COLLISION RESULTS
// ============================================================================

/** Single contact point in a collision */
export interface ContactPoint2D {
  /** World-space contact position */
  point: PhysVec2;
  /** Contact normal (from A to B) */
  normal: PhysVec2;
  /** Penetration depth (negative = separated) */
  separation: number;
  /** Normal impulse applied */
  normalImpulse: number;
  /** Tangent impulse applied (friction) */
  tangentImpulse: number;
}

/** Collision manifold between two colliders */
export interface CollisionManifold {
  /** Body A index */
  bodyIndexA: number;
  /** Body B index */
  bodyIndexB: number;
  /** Collider A identifier */
  colliderA: number;
  /** Collider B identifier */
  colliderB: number;
  /** Contact normal (world space, A→B) */
  normal: PhysVec2;
  /** Number of contact points (1 or 2) */
  contactCount: number;
  /** Contact points */
  contacts: ContactPoint2D[];
  /** Combined friction (geometric mean of materials) */
  friction: number;
  /** Combined restitution (max of materials) */
  restitution: number;
}

/** Raycast hit result */
export interface RaycastHit2D {
  /** Hit point in world space */
  point: PhysVec2;
  /** Surface normal at hit point */
  normal: PhysVec2;
  /** Distance from ray origin */
  distance: number;
  /** Fraction along ray [0..1] */
  fraction: number;
  /** Hit body index */
  bodyIndex: number;
  /** Hit collider index */
  colliderIndex: number;
  /** GameObject GUID of hit object */
  gameObjectGuid: string;
}

/** Overlap query result */
export interface OverlapResult2D {
  /** Body index */
  bodyIndex: number;
  /** Collider index */
  colliderIndex: number;
  /** GameObject GUID */
  gameObjectGuid: string;
}

// ============================================================================
// EVENT TYPES
// ============================================================================

/** Collision event (enter/stay/exit) */
export interface CollisionEvent2D {
  /** The other game object's GUID */
  otherGuid: string;
  /** The other game object's body index */
  otherBodyIndex: number;
  /** Contact manifold */
  manifold: CollisionManifold;
  /** Relative velocity at impact */
  relativeVelocity: PhysVec2;
}

/** Trigger event (enter/stay/exit) */
export interface TriggerEvent2D {
  /** The other game object's GUID */
  otherGuid: string;
  /** The other game object's body index */
  otherBodyIndex: number;
}

// ============================================================================
// BODY STATE (CPU-side, mirrors GPU layout)
// ============================================================================

/** Full rigid body state for simulation */
export interface PhysicsBodyState {
  // Identity
  bodyIndex: number;
  gameObjectId: number;
  guid: string;

  // Type
  bodyType: BodyType;
  sleepState: SleepState;

  // Transform (synced from/to GameObject Transform)
  position: PhysVec2;
  rotation: number; // radians

  // Velocity
  linearVelocity: PhysVec2;
  angularVelocity: number;

  // Mass
  mass: number;
  invMass: number;
  inertia: number;
  invInertia: number;
  centerOfMass: PhysVec2;
  density: number; // Effective density (mass/area) for buoyancy calculations

  // Damping
  linearDamping: number;
  angularDamping: number;

  // Gravity
  gravityScale: number;

  // Constraints (packed bits)
  constraints: number;

  // Collision
  collisionDetection: CollisionDetection2D;
  interpolation: RigidBodyInterpolation;

  // Layers
  layerMask: number;

  // Sleep timer
  sleepTimer: number;

  // Forces accumulated this frame
  forceAccum: PhysVec2;
  torqueAccum: number;
}

// ============================================================================
// COLLIDER STATE
// ============================================================================

/** Collider shape data (CPU-side) */
export interface ColliderState {
  colliderIndex: number;
  bodyIndex: number;
  gameObjectId: number;

  // Shape
  type: ColliderType;
  isTrigger: boolean;

  // Local offset from body center
  offset: PhysVec2;
  rotation: number;

  // Shape-specific data
  halfExtents: PhysVec2;    // Box: half-width, half-height
  radius: number;            // Circle/Capsule radius
  capsuleHeight: number;     // Capsule total height
  capsuleDirection: CapsuleDirection;

  // Polygon (convex hull, max MAX_POLYGON_VERTICES)
  vertices: PhysVec2[];
  normals: PhysVec2[];
  vertexCount: number;

  // Material
  material: PhysicsMaterial2D;

  // Layers
  layerMask: number;
  collisionMask: number;

  // One-way platform
  oneWay: boolean;
  oneWayDirection: PhysVec2;

  // Computed AABB (updated each frame)
  aabbMin: PhysVec2;
  aabbMax: PhysVec2;
}

// ============================================================================
// WORLD CONFIG
// ============================================================================

/** Physics simulation configuration */
export interface PhysicsWorldConfig {
  gravity: PhysVec2;
  fixedTimestep: number;
  velocityIterations: number;
  positionIterations: number;
  maxBodies: number;
  maxColliders: number;
  maxContacts: number;
  allowSleeping: boolean;
  broadPhaseGrid: number; // spatial hash cell size
}

// ============================================================================
// HELPERS
// ============================================================================

/** Create default material */
export function defaultMaterial(): PhysicsMaterial2D {
  return { friction: 0.4, bounciness: 0.0, density: 1.0 };
}

/** Pack constraint flags into u32 */
export function packConstraints(c: RigidBodyConstraints): number {
  return (
    (c.freezePositionX ? 1 : 0) |
    (c.freezePositionY ? 2 : 0) |
    (c.freezeRotation ? 4 : 0)
  );
}

/** Unpack constraint flags from u32 */
export function unpackConstraints(bits: number): RigidBodyConstraints {
  return {
    freezePositionX: (bits & 1) !== 0,
    freezePositionY: (bits & 2) !== 0,
    freezeRotation: (bits & 4) !== 0,
  };
}

/** Validate convex polygon vertices (must be convex, CCW, ≤ MAX_POLYGON_VERTICES) */
export function validateConvexPolygon(vertices: PhysVec2[]): { valid: boolean; error?: string } {
  if (vertices.length < 3) {
    return { valid: false, error: `Polygon needs ≥3 vertices, got ${vertices.length}` };
  }
  if (vertices.length > MAX_POLYGON_VERTICES) {
    return { valid: false, error: `Polygon exceeds max ${MAX_POLYGON_VERTICES} vertices (got ${vertices.length})` };
  }

  // Check convexity via cross products (all must be same sign)
  let sign = 0;
  const n = vertices.length;
  for (let i = 0; i < n; i++) {
    const a = vertices[i];
    const b = vertices[(i + 1) % n];
    const c = vertices[(i + 2) % n];
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (Math.abs(cross) < 1e-7) continue; // collinear edge
    const s = cross > 0 ? 1 : -1;
    if (sign === 0) {
      sign = s;
    } else if (s !== sign) {
      return { valid: false, error: 'Polygon is not convex' };
    }
  }
  if (sign === 0) {
    return { valid: false, error: 'Polygon is degenerate (all collinear)' };
  }

  return { valid: true };
}

/** Compute convex hull normals from CCW vertices */
export function computePolygonNormals(vertices: PhysVec2[]): PhysVec2[] {
  const normals: PhysVec2[] = [];
  const n = vertices.length;
  for (let i = 0; i < n; i++) {
    const a = vertices[i];
    const b = vertices[(i + 1) % n];
    const edgeX = b.x - a.x;
    const edgeY = b.y - a.y;
    const len = Math.sqrt(edgeX * edgeX + edgeY * edgeY);
    if (len > 1e-7) {
      normals.push({ x: edgeY / len, y: -edgeX / len });
    } else {
      normals.push({ x: 0, y: 1 });
    }
  }
  return normals;
}

/** Compute AABB for a shape at given transform */
export function computeColliderAABB(
  collider: ColliderState,
  bodyPos: PhysVec2,
  bodyRot: number,
): { min: PhysVec2; max: PhysVec2 } {
  const cos = Math.cos(bodyRot + collider.rotation);
  const sin = Math.sin(bodyRot + collider.rotation);

  // Transform offset to world
  const cx = bodyPos.x + collider.offset.x * cos - collider.offset.y * sin;
  const cy = bodyPos.y + collider.offset.x * sin + collider.offset.y * cos;

  switch (collider.type) {
    case ColliderType.Circle: {
      const r = collider.radius;
      return { min: { x: cx - r, y: cy - r }, max: { x: cx + r, y: cy + r } };
    }
    case ColliderType.Box: {
      const hx = collider.halfExtents.x;
      const hy = collider.halfExtents.y;
      // Rotated box AABB
      const absCos = Math.abs(cos);
      const absSin = Math.abs(sin);
      const ex = hx * absCos + hy * absSin;
      const ey = hx * absSin + hy * absCos;
      return { min: { x: cx - ex, y: cy - ey }, max: { x: cx + ex, y: cy + ey } };
    }
    case ColliderType.Capsule: {
      const r = collider.radius;
      const halfH = collider.capsuleHeight * 0.5;
      let ex: number, ey: number;
      if (collider.capsuleDirection === CapsuleDirection.Vertical) {
        const absCos = Math.abs(cos);
        const absSin = Math.abs(sin);
        ex = r * absCos + halfH * absSin;
        ey = r * absSin + halfH * absCos;
      } else {
        const absCos = Math.abs(cos);
        const absSin = Math.abs(sin);
        ex = halfH * absCos + r * absSin;
        ey = halfH * absSin + r * absCos;
      }
      return { min: { x: cx - ex, y: cy - ey }, max: { x: cx + ex, y: cy + ey } };
    }
    case ColliderType.Polygon: {
      let minX = Infinity, minY = Infinity;
      let maxX = -Infinity, maxY = -Infinity;
      for (let i = 0; i < collider.vertexCount; i++) {
        const v = collider.vertices[i];
        const wx = cx + v.x * cos - v.y * sin;
        const wy = cy + v.x * sin + v.y * cos;
        if (wx < minX) minX = wx;
        if (wy < minY) minY = wy;
        if (wx > maxX) maxX = wx;
        if (wy > maxY) maxY = wy;
      }
      return { min: { x: minX, y: minY }, max: { x: maxX, y: maxY } };
    }
    default: {
      // Fallback: unit AABB
      return { min: { x: cx - 0.5, y: cy - 0.5 }, max: { x: cx + 0.5, y: cy + 0.5 } };
    }
  }
}
